import { useEffect, useState, useRef } from "react";
import {
  Space,
  Table,
  Tag,
  Pagination,
  Tooltip,
  Select,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Popconfirm,
  Alert,
} from "antd";
import type { TableProps } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  SendOutlined,
  NotificationOutlined,
  CloseOutlined,
  SaveOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import BaseService from "../../../service/base.service";
import PaginationResponse from "../../../models/paginationResponse";
import NotificaionsTemplate from "../../../models/notificationsTemplate/notificaionsTemplate";
import NotificaionTemplateDetails from "../../../models/notificationsTemplate/NotificaionTemplateDetails";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useAppDispatch } from "../../../hooks/redux-hooks";
import {
  AudienceType,
  AudienceTypeLabel,
  audienceColors,
} from "./components/notificationConstants";
import notify from "../../../components/common/notificationHelper";
import SendNotificationModal from "./components/SendNotificationModal";

interface FormValues {
  id: string;
  name: string;
  title: string;
  message: string;
  audienceType: string;
  importance: string;
  active: boolean;
}

const { Option } = Select;
const PAGE_SIZE = 10;

const NotificationsTemplatePage = () => {
  const isMobile = useIsMobile();
  const [listNotificaionsTemplate, setLisNotificaionsTemplate] = useState<NotificaionsTemplate[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [sendModalVisible, setSendModalVisible] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<NotificaionsTemplate | null>(null);
  const dispatch = useAppDispatch();
  const [form] = Form.useForm<FormValues>();
  const horizontalPadding = isMobile ? 12 : 24;
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableScrollY, setTableScrollY] = useState<number>(400);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const calculateTableScrollHeight = () => {
      if (tableContainerRef.current) {
        const containerHeight = tableContainerRef.current.clientHeight;
        const offset = isMobile ? 72 : 100;
        setTableScrollY(Math.max(containerHeight - offset, 200));
      }
    };
    calculateTableScrollHeight();
    window.addEventListener("resize", calculateTableScrollHeight);
    return () => window.removeEventListener("resize", calculateTableScrollHeight);
  }, [isMobile]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchText(searchInput);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    handleGetAllNotificationsTemplate(currentPage, searchText);
  }, [currentPage, searchText]);

  useEffect(() => {
    if (isModalOpen) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (contentRef.current) {
            contentRef.current.scrollTop = 0;
          } else {
            const modalBody = document.querySelector(".ant-modal-body") as HTMLElement | null;
            if (modalBody) modalBody.scrollTop = 0;
          }
        }, 0);
      });
    }
  }, [isModalOpen]);

  const handleGetAllNotificationsTemplate = async (page: number, search: string) => {
    setLoading(true);
    try {
      let url = `/notifications-templates?page=${page - 1}&size=${PAGE_SIZE}`;
      if (search && search.trim() !== "") {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }
      const rp = await BaseService.getAll<PaginationResponse<NotificaionsTemplate>>(url);
      if (rp.Status) {
        const paginationResponse = rp.Data || [];
        const notificaionsTemplateList = paginationResponse.data.map(
          (n: any) =>
            new NotificaionsTemplate(
              n.id,
              n.name,
              n.title,
              n.importance,
              n.audienceType,
              n.active
            )
        );
        setLisNotificaionsTemplate(notificaionsTemplateList);
        setTotalElements(paginationResponse.totalElements);
        setCurrentPage(page);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNotificaionTemplate = (id: any, title: string) => {
    BaseService.delete("/notifications-templates/", id).then((rp) => {
      if (rp.Status) {
        handleGetAllNotificationsTemplate(currentPage, searchText);
        notify({
          title: "Notification deleted successfully",
          description: `${title} template has been deleted.`,
          variant: "info",
        });
      } else {
        notify({
          title: "Error deleting notification",
          description: `Failed to delete ${title}.`,
          variant: "error",
        });
      }
    });
  };

  const handleAddAndEditUser = (values: FormValues) => {
    const request = isEdit
      ? BaseService.update("/notifications-templates/", values.id, values)
      : BaseService.create("/notifications-templates", values);

    request.then((rp) => {
      if (rp.Status) {
        notify({
          title: isEdit
            ? "Notification updated successfully"
            : "Notification added successfully",
          description: `${values.title} has been ${isEdit ? "updated" : "saved"
            }.`,
          variant: "success",
        });
        handleGetAllNotificationsTemplate(currentPage, searchText);
      } else {
        notify({
          title: "Error saving notification",
          description: `Failed to save ${values.title}.`,
          variant: "error",
        });
      }
    });

    setIsModalOpen(false);
  };

  const handleSendClick = (template: NotificaionsTemplate) => {
    setSelectedTemplate(template);
    setSendModalVisible(true);
  };

  const handleEdit = async (template: NotificaionsTemplate) => {
    try {
      const rp = await BaseService.get<NotificaionTemplateDetails>(
        "/notifications-templates/",
        template.id
      );
      if (rp.Status) {
        setIsEdit(true);
        form.setFieldsValue(rp.Data);
        setIsModalOpen(true);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleAdd = () => {
    setIsEdit(false);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleCancel = () => setIsModalOpen(false);
  const handlePageChange = (page: number) => setCurrentPage(page);

  const renderAudience = (type?: string | null) => {
    if (!type || !(type in AudienceTypeLabel)) {
      return <Tag color="default">N/A</Tag>;
    }
    const audienceType = type as AudienceType;
    return <Tag color={audienceColors[audienceType]}>{AudienceTypeLabel[audienceType]}</Tag>;
  };

  const renderImportance = (importance: string) => {
    const color =
      importance === "HIGH" ? "red" : importance === "NORMAL" ? "blue" : "green";
    return <Tag color={color}>{importance}</Tag>;
  };

  const renderActions = (record: NotificaionsTemplate) => (
    <Space size="middle">
      <Tooltip title="Edit Template">
        <EditOutlined
          style={{ cursor: "pointer" }}
          onClick={() => handleEdit(record)}
        />
      </Tooltip>
      <Popconfirm
        title={`Are you sure you want to delete "${record.title}"?`}
        onConfirm={() =>
          handleDeleteNotificaionTemplate(record.id, record.title)
        }
        okText="Yes"
        cancelText="No"
        okButtonProps={{ danger: true }}
      >
        <Tooltip title="Delete Template">
          <DeleteOutlined style={{ color: "#ff4d4f", cursor: "pointer" }} />
        </Tooltip>
      </Popconfirm>
      <Tooltip
        title={record.active ? "Send Notification" : "Template is inactive"}
      >
        <SendOutlined
          style={{
            color: record.active ? "#3B6FFF" : "#ccc",
            cursor: record.active ? "pointer" : "not-allowed",
          }}
          onClick={() => record.active && handleSendClick(record)}
        />
      </Tooltip>
    </Space>
  );

  const columns: TableProps<NotificaionsTemplate>["columns"] = isMobile
    ? [
        {
          title: "Template",
          key: "template",
          render: (_: unknown, record: NotificaionsTemplate) => (
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: "#1f2937" }}>
                {record.title}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "#6b7280",
                  marginTop: 2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {record.name}
              </div>
              <div style={{ marginTop: 6 }}>
                {renderAudience(record.audienceType)}
                {renderImportance(record.importance)}
                <Tag color={record.active ? "success" : "default"}>
                  {record.active ? "Active" : "Inactive"}
                </Tag>
              </div>
            </div>
          ),
        },
        {
          title: "",
          key: "action",
          width: 96,
          align: "right",
          render: (_: unknown, record: NotificaionsTemplate) =>
            renderActions(record),
        },
      ]
    : [
        { title: "ID", dataIndex: "id", key: "id" },
        { title: "Name", dataIndex: "name", key: "name" },
        { title: "Title", dataIndex: "title", key: "title" },
        {
          title: "Audience",
          dataIndex: "audienceType",
          key: "audienceType",
          render: renderAudience,
        },
        {
          title: "Importance",
          dataIndex: "importance",
          key: "importance",
          render: renderImportance,
        },
        {
          title: "Active",
          dataIndex: "active",
          key: "active",
          render: (active: boolean) => (active ? "✅ Yes" : "❌ No"),
        },
        {
          title: "Action",
          key: "actions",
          render: (_: unknown, record: NotificaionsTemplate) =>
            renderActions(record),
        },
      ];

  return (
    <>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          height: "100%",
          padding: isMobile ? "8px 0 16px" : "16px 16px 24px",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <div style={{ marginBottom: isMobile ? 12 : 24, flexShrink: 0 }}>
          <Alert
            message={
              isMobile
                ? "Manage templates, audiences, and send notifications."
                : "Manage your notification templates, audiences, and send notifications from here."
            }
            type="info"
            showIcon={!isMobile}
            style={{
              background: "#f0f7ff",
              borderColor: "#d0e4ff",
              color: "#2c5282",
              borderRadius: 8,
              padding: isMobile ? "8px 12px" : "10px 14px",
              fontSize: isMobile ? 13 : 14,
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            justifyContent: "space-between",
            gap: isMobile ? 10 : 12,
            background: "#fff",
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            padding: isMobile ? "10px 12px" : "12px 16px",
            marginBottom: isMobile ? 12 : 16,
            boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
            flexShrink: 0,
          }}
        >
          <Input
            placeholder="Search by name, title or message"
            allowClear
            style={{ width: isMobile ? "100%" : 320 }}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />

          <Button
            type="primary"
            onClick={handleAdd}
            icon={<PlusOutlined />}
            block={isMobile}
            style={{
              borderRadius: 6,
              padding: "6px 18px",
              fontWeight: 500,
            }}
          >
            Add Notification
          </Button>
        </div>

        <div
          ref={tableContainerRef}
          style={{
            flex: 1,
            minHeight: 0,
            background: "#fff",
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
            padding: isMobile ? 10 : 16,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Table
            columns={columns}
            dataSource={listNotificaionsTemplate}
            pagination={false}
            scroll={{ y: tableScrollY, x: isMobile ? undefined : true }}
            sticky
            bordered={false}
            loading={loading}
            locale={{ emptyText: "No notification templates found" }}
            rowKey="id"
            size={isMobile ? "small" : "middle"}
          />
          <SendNotificationModal
            visible={sendModalVisible}
            template={selectedTemplate}
            onClose={() => setSendModalVisible(false)}
          />
        </div>

        {/* Pagination */}
        <div
          style={{
            marginTop: isMobile ? 12 : 20,
            display: "flex",
            justifyContent: "center",
            paddingBottom: isMobile ? 8 : 24,
            flexShrink: 0,
          }}
        >
          <Pagination
            current={currentPage}
            pageSize={PAGE_SIZE}
            total={totalElements}
            onChange={handlePageChange}
            showSizeChanger={false}
            size={isMobile ? "small" : "default"}
            simple={isMobile}
          />
        </div>
      </div>

      {/* Modal */}
      <Modal
        title={
          <div
            style={{
              textAlign: "center",
              fontWeight: 600,
              fontSize: isMobile ? "1.2rem" : "1.5rem",
              color: "#1f1f1f",
              borderBottom: "1px solid #f0f0f0",
              paddingBottom: 12,
            }}
          >
            {isEdit ? (
              <>
                <EditOutlined style={{ color: "#1677ff", marginRight: 8 }} />
                Edit Notification Template
              </>
            ) : (
              <>
                <NotificationOutlined
                  style={{ color: "#52c41a", marginRight: 8 }}
                />
                Add Notification Template
              </>
            )}
            <div
              style={{
                fontSize: isMobile ? 13 : 14,
                fontWeight: 400,
                color: "#6b7280",
                marginTop: 4,
              }}
            >
              {isEdit
                ? "Update notification template details below"
                : "Fill in the details to add a new template"}
            </div>
          </div>
        }
        centered
        open={isModalOpen}
        footer={null}
        width={isMobile ? "calc(100vw - 24px)" : "60vw"}
        confirmLoading={loading}
        onCancel={handleCancel}
        styles={{
          body: {
            maxHeight: isMobile ? "75vh" : "80vh",
            overflowY: "auto",
            position: "relative",
            padding: isMobile ? "12px 12px 0" : undefined,
          },
        }}
      >
        <div
          ref={contentRef}
          style={{
            maxHeight: isMobile ? "55vh" : "65vh",
            overflowY: "auto",
            paddingRight: 8,
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Form<FormValues>
            form={form}
            name="validateOnly"
            layout="vertical"
            autoComplete="off"
            onFinish={handleAddAndEditUser}
            style={{ padding: `16px ${horizontalPadding}px 0` }}
          >
            {isEdit && (
              <Form.Item name="id" label="ID" rules={[{ required: true }]}>
                <Input disabled />
              </Form.Item>
            )}
            <Form.Item name="name" label="Name" rules={[{ required: true }]}>
              <Input placeholder="e.g. WELCOME_USER" />
            </Form.Item>
            <Form.Item name="title" label="Title" rules={[{ required: true }]}>
              <Input placeholder="e.g. Welcome to the App!" />
            </Form.Item>
            <Form.Item
              label="Message"
              name="message"
              rules={[{ required: true }]}
            >
              <Input.TextArea rows={4} placeholder="Enter message..." />
            </Form.Item>
            <Form.Item
              label="Importance"
              name="importance"
              rules={[{ required: true }]}
            >
              <Select placeholder="Select importance">
                <Option value="LOW">Low</Option>
                <Option value="NORMAL">Normal</Option>
                <Option value="HIGH">High</Option>
              </Select>
            </Form.Item>
            <Form.Item
              label="Audience Type"
              name="audienceType"
              rules={[{ required: true }]}
            >
              <Select>
                {Object.values(AudienceType).map((type) => (
                  <Select.Option key={type} value={type}>
                    {AudienceTypeLabel[type]}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="active" label="Active" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          </Form>
        </div>

        <div
          style={{
            position: "sticky",
            bottom: 0,
            background: "#fff",
            padding: isMobile ? "12px 8px" : "16px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            borderTop: "1px solid #f0f0f0",
            boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.04)",
            zIndex: 10,
          }}
        >
          <Button onClick={handleCancel} danger icon={<CloseOutlined />}>
            Cancel
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={() => form.submit()}
            loading={loading}
          >
            {isEdit ? "Update" : "Save"}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default NotificationsTemplatePage;
